import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { BuildingApartment as BuildingApartmentIcon } from '@phosphor-icons/react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';

import type { Unit } from '@/types/v2/unit';
import { ProfessionalDialog } from '@/components/common';
import { updateUnit } from '@/api/units';

// US States for the dropdown
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

// Form validation schema
const editUnitSchema = z.object({
  unit_number: z.string().optional(),
  address: z.string().min(1, 'Address is required'),
  city: z.string().optional(),
  state: z.string().optional(),
  zip_code: z.string().optional(),
  floor: z.string().optional(),
});

type EditUnitFormData = z.infer<typeof editUnitSchema>;

interface EditUnitDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  unit: Unit;
}

export function EditUnitDialog({ open, onClose, onSuccess, unit }: EditUnitDialogProps): React.JSX.Element {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<EditUnitFormData>({
    resolver: zodResolver(editUnitSchema),
    defaultValues: {
      unit_number: unit.unit_number || '',
      address: unit.address || '',
      city: unit.city || '',
      state: unit.state || '',
      zip_code: unit.zip_code || '',
      floor: unit.floor || '',
    },
  });

  // Reset form when unit changes
  React.useEffect(() => {
    reset({
      unit_number: unit.unit_number || '',
      address: unit.address || '',
      city: unit.city || '',
      state: unit.state || '',
      zip_code: unit.zip_code || '',
      floor: unit.floor || '',
    });
  }, [unit, reset]);

  const onSubmit = async (data: EditUnitFormData) => {
    setIsSubmitting(true);
    try {
      const response = await updateUnit(unit.id, data);
      
      toast.success('Unit updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error updating unit:', error);
      toast.error(error?.response?.data?.message || 'Failed to update unit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isDirty) {
      // Ask for confirmation if form has changes
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        reset();
        onClose();
      }
    } else {
      onClose();
    }
  };

  return (
    <ProfessionalDialog
      fullWidth
      icon={<BuildingApartmentIcon />}
      maxWidth="md"
      onClose={handleClose}
      open={open}
      subtitle="Update the unit information below"
      title="Edit Unit Details"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={3}>
            {/* Unit Number */}
            <Controller
              control={control}
              name="unit_number"
              render={({ field }) => (
                <TextField
                  {...field}
                  error={Boolean(errors.unit_number)}
                  fullWidth
                  helperText={errors.unit_number?.message || "Leave blank if no unit number"}
                  label="Unit Number (Optional)"
                  placeholder="e.g., 101, A1, Ground Floor"
                />
              )}
            />

            {/* Address */}
            <Controller
              control={control}
              name="address"
              render={({ field }) => (
                <TextField
                  {...field}
                  error={Boolean(errors.address)}
                  fullWidth
                  helperText={errors.address?.message}
                  label="Street Address"
                  placeholder="e.g., 12423 Black Foot Street"
                />
              )}
            />

            {/* Floor */}
            <Controller
              control={control}
              name="floor"
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Floor (Optional)"
                  placeholder="e.g., 1st Floor, Basement"
                />
              )}
            />

            <Divider />

            {/* City, State, ZIP Row */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {/* City */}
              <Controller
                control={control}
                name="city"
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={Boolean(errors.city)}
                    fullWidth
                    helperText={errors.city?.message}
                    label="City (Optional)"
                    placeholder="e.g., Riverton"
                  />
                )}
              />

              {/* State */}
              <Controller
                control={control}
                name="state"
                render={({ field }) => (
                  <FormControl error={Boolean(errors.state)} fullWidth>
                    <InputLabel>State (Optional)</InputLabel>
                    <Select
                      {...field}
                      label="State (Optional)"
                    >
                      {US_STATES.map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.state ? <Typography color="error" sx={{ mt: 0.5, display: 'block' }} variant="caption">
                        {errors.state.message}
                      </Typography> : null}
                  </FormControl>
                )}
              />

              {/* ZIP Code */}
              <Controller
                control={control}
                name="zip_code"
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={Boolean(errors.zip_code)}
                    fullWidth
                    helperText={errors.zip_code?.message}
                    label="ZIP Code (Optional)"
                    placeholder="e.g., 84096"
                  />
                )}
              />
            </Stack>

            {/* Preview Card */}
            <Card sx={{ backgroundColor: 'grey.50', borderColor: 'primary.main' }} variant="outlined">
              <CardContent>
                <Typography color="primary.main" sx={{ mb: 1, fontWeight: 600 }} variant="subtitle2">
                  Preview
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  {control._formValues.address}
                  {control._formValues.city ? `, ${control._formValues.city}` : null}
                  {control._formValues.state ? `, ${control._formValues.state}` : null}
                  {control._formValues.zip_code ? ` ${control._formValues.zip_code}` : null}
                </Typography>
                {control._formValues.unit_number ? <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
                    Unit: {control._formValues.unit_number}
                  </Typography> : null}
                {control._formValues.floor ? <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
                    Floor: {control._formValues.floor}
                  </Typography> : null}
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            disabled={isSubmitting}
            onClick={handleClose}
            sx={{ minWidth: 100 }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            disabled={isSubmitting || !isDirty}
            sx={{ minWidth: 100 }}
            type="submit"
            variant="contained"
          >
            {isSubmitting ? 'Updating...' : 'Update Unit'}
          </Button>
        </DialogActions>
      </form>
    </ProfessionalDialog>
  );
}
